import { addDays, mondayOf } from './checkin-weeks';
import { participantChannels, TrackerConfig } from './tracker-config';
import {
  rollupRange,
  rollupWeek,
  scoreboard,
  TrackerParticipantInput,
  TrackerRollup,
  TrackerSpendInput,
} from './tracker-rollup';
import { leadsSetUpLine, leadsText, spendInput, TrackerSpendEntry } from './tracker-leads';
import {
  decisionOn,
  fmtMoney,
  fmtShortDate,
  fmtTrackerDate,
  nextStepName,
  personName,
  inBetGroup,
  personOfferText,
  touchesOfGroup,
  trackerBetGroups,
  betLabel,
  sortTouches,
  stageRole,
  touchesByPerson,
  trackerCountsLine,
  trackerGroup,
  TrackerBet,
  TrackerPerson,
  TrackerTouch,
} from './tracker-people';

/** What the summaries read: the Tracker tab's data, or the server's own copy of it. */
export type TrackerSummaryData = {
  config: TrackerConfig;
  participant: TrackerParticipantInput;
  people: TrackerPerson[];
  touches: TrackerTouch[];
  /** The participant's bets, on a course with bets on. */
  bets?: TrackerBet[];
  /** The participant's spend entries, on a Leads course. */
  spend?: TrackerSpendEntry[];
  today: string;
  courseTitle: string;
};

/**
 * The participant's spend, priced at their rate, on a Leads course; undefined
 * elsewhere, so rollups leave spend out.
 */
export function trackerSpend(
  data: Pick<TrackerSummaryData, 'config' | 'participant' | 'spend'>,
): TrackerSpendInput | undefined {
  return participantChannels(data.config)
    ? spendInput(data.participant, data.spend ?? [])
    : undefined;
}

/**
 * The groups a per-bet breakdown lists (trackerBetGroups), when there are
 * two or more: one group would repeat the total.
 */
export function breakdownBets(
  data: Pick<TrackerSummaryData, 'config' | 'bets'> & {
    people: Array<{ id: string; betId?: string }>;
  },
) {
  const groups = trackerBetGroups(data.config, data.bets, data.people);
  return groups.length >= 2 ? groups : [];
}

/**
 * "Bets: Coaches · 90-day coaching package, Consultants · Pricing audit", so
 * a coach sees what each bet is. Undefined with bets off or none yet.
 */
export function betsListLine(data: Pick<TrackerSummaryData, 'config' | 'bets'>) {
  const bets = data.config.bets ? (data.bets ?? []) : [];
  return bets.length ? `Bets: ${bets.map(betLabel).join(', ')}` : undefined;
}

/** One group's share of the data: its people and their touches, for the usual maths. */
export function betData<T extends TrackerSummaryData>(data: T, groupId: string): T {
  return {
    ...data,
    people: data.people.filter((p) => inBetGroup(data.bets, p, groupId)),
    touches: touchesOfGroup(data.bets, data.people, data.touches, groupId),
  };
}

/** "Coaches: Sends 5 · Replies 2 · Conversations held 1 · Closes 0", one per bet. */
export function betRollupLines(
  data: TrackerSummaryData,
  rollupOf: (touches: TrackerTouch[]) => TrackerRollup,
) {
  const closeAs = data.participant.closeAs;
  return breakdownBets(data).map(
    (bet) =>
      `${bet.name}: ${trackerCountsLine(
        data.config,
        rollupOf(touchesOfGroup(data.bets, data.people, data.touches, bet.id)),
        { withCloses: true, closeAs },
      )}`,
  );
}

/**
 * One bet's scoreboard figure: the amount on the participant's close choice,
 * or its leads on cost per lead. Undefined before a close choice, as the
 * whole scoreboard is "Not set up yet" then.
 */
function betScore(data: TrackerSummaryData) {
  const board = scoreboard(data.config, data.touches, data.participant);
  if (board.kind === 'cpl') {
    return leadsText(board.leads);
  }
  if (!data.participant.closeAs) {
    return;
  }
  return fmtMoney(board.progressCents);
}

/**
 * "$3,200 booked" for one bet, or "4 leads"; undefined before a close
 * choice. `touches` are the bet's (touchesOfGroup).
 */
export function betScoreboardLine(data: TrackerSummaryData, touches: TrackerTouch[]) {
  const score = betScore({ ...data, touches });
  if (score === undefined) {
    return;
  }
  return data.config.scoreboard.kind === 'cpl' ? score : `${score} ${data.participant.closeAs}`;
}

/**
 * The line under the scoreboard's bar: "Coaches $3,200 · Consultants $1,600
 * · Speakers $0". One target covers the whole tracker, so the bets carry
 * amounts only. Undefined with fewer than two bets or no close choice.
 */
export function betsScoreboardLine(data: TrackerSummaryData) {
  const parts: string[] = [];
  for (const bet of breakdownBets(data)) {
    const score = betScore(betData(data, bet.id));
    if (score === undefined) {
      return;
    }
    parts.push(`${bet.name} ${score}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
}

/** The scoreboard in one line, as the card's headline reads. */
export function scoreboardLine(data: TrackerSummaryData) {
  const board = scoreboard(data.config, data.touches, data.participant, data.spend);
  if (board.kind === 'cpl') {
    if (board.targetCents === undefined) {
      return 'Not set up yet';
    }
    const target = `target ${fmtMoney(board.targetCents)} or less`;
    if (board.cplCents !== undefined) {
      return `${fmtMoney(board.cplCents)} per lead (${target})`;
    }
    return board.leads ? `${leadsText(board.leads)}, no spend added` : `No leads yet (${target})`;
  }
  if (!data.participant.closeAs) {
    return 'Not set up yet';
  }
  return `${fmtMoney(board.progressCents)} ${data.participant.closeAs} of ${fmtMoney(
    data.participant.targetCents ?? 0,
  )}`;
}

function rollupLines(
  config: TrackerConfig,
  now: TrackerRollup,
  before?: { rollup: TrackerRollup; label: string },
) {
  const labels = config.rollupLabels;
  const lines: string[] = [];
  function compared(value: number, earlier?: number) {
    return before ? `${value} (${before.label} ${earlier ?? 0})` : `${value}`;
  }
  const sequence = config.channels.some((c) => c.flow === 'sequence');
  if (sequence) {
    const b = before?.rollup;
    lines.push(`${labels.sends}: ${compared(now.sends, b?.sends)}`);
    lines.push(`${labels.replies}: ${compared(now.replies, b?.replies)}`);
    lines.push(`${labels.held}: ${compared(now.held, b?.held)}, ${now.booked} booked`);
    lines.push(`${labels.closes}: ${compared(now.closes, b?.closes)}`);
  }
  for (const channel of config.channels.filter((c) => c.flow === 'steps')) {
    for (const stage of channel.stages) {
      const label = stage.short || stage.label;
      lines.push(
        `${channel.label} ${label}: ${compared(
          now.byStage[stage.key] ?? 0,
          before?.rollup.byStage[stage.key],
        )}`,
      );
    }
  }
  if (participantChannels(config)) {
    lines.push(`Leads: ${compared(now.leads, before?.rollup.leads)}`);
    if (now.spendCents !== undefined) {
      const earlier = before?.rollup.spendCents;
      lines.push(
        `Spend: ${fmtMoney(now.spendCents)}${
          before && earlier !== undefined ? ` (${before.label} ${fmtMoney(earlier)})` : ''
        }`,
      );
    }
  }
  return lines;
}

/** The period Copy for Claude reports: a week, or the immersion sprint so far. */
export type TrackerCopyPeriod = { weekStart: string } | { start: string; end: string };

/** Plain text for the participant to paste into their Claude chat. */
export function copyForClaude(data: TrackerSummaryData, period?: TrackerCopyPeriod) {
  const { config, today, participant } = data;
  const closeAs = participant.closeAs;
  const byPerson = touchesByPerson(data.touches);
  const spend = trackerSpend(data);
  let heading: string;
  let totals: string[];
  let betLines: string[];
  if (period && 'start' in period) {
    heading = `Immersion sprint, ${fmtShortDate(period.start)} to ${fmtShortDate(period.end)}`;
    totals = rollupLines(
      config,
      rollupRange(config, data.touches, closeAs, period.start, period.end, spend),
    );
    betLines = betRollupLines(data, (touches) =>
      rollupRange(config, touches, closeAs, period.start, period.end),
    );
  } else {
    const week = period?.weekStart ?? today;
    const isPastWeek = addDays(mondayOf(week), 6) < today;
    const before = rollupWeek(config, data.touches, closeAs, addDays(week, -7), spend);
    heading = isPastWeek
      ? `Week of ${fmtShortDate(mondayOf(week))} (the week before)`
      : 'This week (last week)';
    totals = rollupLines(config, rollupWeek(config, data.touches, closeAs, week, spend), {
      rollup: before,
      label: isPastWeek ? 'week before' : 'last week',
    });
    betLines = betRollupLines(data, (touches) => rollupWeek(config, touches, closeAs, week));
  }
  const betsLine = betsScoreboardLine(data);
  const setUpLine = participantChannels(config) ? leadsSetUpLine(data.participant) : undefined;
  const lines = [
    `${data.courseTitle} · tracker · ${fmtTrackerDate(today, today)}`,
    ...(setUpLine ? [setUpLine] : []),
    '',
    heading,
    ...totals,
    ...(betLines.length ? ['', ...betLines] : []),
    '',
    `Scoreboard: ${scoreboardLine(data)}`,
    ...(betsLine ? [`By bet: ${betsLine}`] : []),
  ];
  const offerOf = (p: TrackerPerson) => personOfferText(config, data.bets, p);
  const groups = data.people.map((p) => ({
    person: p,
    group: trackerGroup(config, p, byPerson.get(p.id) ?? [], participant.closeAs, today),
  }));
  function personLine(p: TrackerPerson, date?: string) {
    return `- ${[
      personName(p),
      offerOf(p),
      nextStepName(config, p),
      date ? fmtTrackerDate(date, today) : '',
    ]
      .filter(Boolean)
      .join(', ')}`;
  }
  for (const [group, title] of [
    ['overdue', 'Overdue'],
    ['due_today', 'Due today'],
  ] as const) {
    const people = groups.filter((g) => g.group === group).map((g) => g.person);
    lines.push('', `${title} (${people.length})`, ...people.map((p) => personLine(p, p.nextOn)));
  }
  const weekAhead = addDays(today, 7);
  const booked = data.people.flatMap((p) => {
    const touches = sortTouches(byPerson.get(p.id) ?? []);
    if (stageRole(config, p.stageKey) !== 'booked') {
      return [];
    }
    const booking = touches.filter((t) => stageRole(config, t.stageKey) === 'booked').pop();
    return booking?.forOn && booking.forOn >= today && booking.forOn <= weekAhead
      ? [{ p, on: booking.forOn }]
      : [];
  });
  lines.push(
    '',
    `Conversations booked in the next 7 days (${booked.length})`,
    ...booked
      .sort((a, b) => (a.on < b.on ? -1 : 1))
      .map(
        ({ p, on }) =>
          `- ${[personName(p), offerOf(p), fmtTrackerDate(on, today)].filter(Boolean).join(', ')}`,
      ),
  );
  const decisions = data.people.flatMap((p) => {
    if (stageRole(config, p.stageKey) !== 'decision') {
      return [];
    }
    return [{ p, on: decisionOn(config, byPerson.get(p.id) ?? []) }];
  });
  lines.push(
    '',
    `Decisions due (${decisions.length})`,
    ...decisions.map(
      ({ p, on }) =>
        `- ${[personName(p), offerOf(p), on ? fmtTrackerDate(on, today) : ''].filter(Boolean).join(', ')}`,
    ),
  );
  return lines.join('\n');
}
